import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    List,
    ListItem,
    ListItemText,
    TextField,
    Typography,
} from '@mui/material';
import {
    OffboardingContractError,
    parseOffboardingApiError,
    permanentlyDeleteTeamMember,
    requestTeamMemberDeletionPreview,
    validateTeamMemberDeletionPreview,
    validateTeamMemberDeletionResult,
} from '../../services/teamMemberOffboardingApi';

const TITLE_ID = 'team-member-offboarding-title';
const DESCRIPTION_ID = 'team-member-offboarding-description';

const WARNING_COPY = {
    POSTGRES_CANONICAL_TARGET_ABSENT:
        'No canonical application database account was found. Review the compatibility-path disclosure below.',
    ROW_COUNT_REVIEW_REQUIRED:
        'This member has an unusually large amount of data. Review the totals carefully before continuing.',
};

const BLOCKER_COPY = {
    DIRECT_ORPHANED_DATABASE_DATA_DETECTED:
        'Resolvable historical database rows were detected. Manual remediation is required before this account can be deleted.',
    ROW_COUNT_HARD_LIMIT_EXCEEDED:
        'The amount of affected data exceeds the approved safety limit.',
    UNBOUNDED_HIGH_VOLUME_QUERY_PLAN:
        'The database cannot confirm that this deletion plan is safely bounded.',
    TARGET_CONFIRMATION_UNAVAILABLE:
        'The target has no server-confirmed email available for confirmation.',
    CROSS_OWNER_REFERENCE_DETECTED:
        'Data associated with another member references this target and must be reviewed first.',
};

const FRESH_PREVIEW_CODES = new Set([
    'POSTGRES_TARGET_APPEARED',
    'CONFIRMATION_MISMATCH',
    'CONFIRMATION_INVALID',
]);

const NON_RETRYABLE_CODES = new Set([
    'COMMIT_OUTCOME_UNKNOWN',
    'ROLLBACK_FAILED_UNSAFE',
]);

const FEATURE_DISABLED_CODES = new Set([
    'OFFBOARDING_DISABLED',
    'OFFBOARDING_EXECUTION_DISABLED',
]);

const FIREBASE_OUTCOME_UNKNOWN_CODE = 'FIREBASE_DELETE_OUTCOME_UNKNOWN_AFTER_DATABASE_PURGE';

function formatCount(value) {
    const count = Number(value);
    return Number.isFinite(count) ? count.toLocaleString() : '0';
}

function safeList(value) {
    return Array.isArray(value) ? value.filter((item) => typeof item === 'string') : [];
}

function friendlyCode(code, copy) {
    return copy[code] || 'The server reported a safety condition that must be reviewed before deletion.';
}

function PreviewSummary({ preview, firebaseWasMissing }) {
    const categories = Array.isArray(preview.categories) ? preview.categories : [];
    const warnings = safeList(preview.warnings);
    const blockers = safeList(preview.blockers);
    const retained = safeList(preview.retained_disclosures);
    const shared = safeList(preview.shared_disclosures);
    const isAuthOnly = preview.preview_kind === 'auth_only';

    return (
        <Box sx={{ display: 'grid', gap: 1.5 }}>
            <Alert severity={isAuthOnly ? 'warning' : 'error'}>
                {isAuthOnly
                    ? 'Authentication-only compatibility path: the website login will be removed, but no database cleanup will run.'
                    : firebaseWasMissing
                        ? 'The website login is already absent. This permanently clears the listed remaining active app data. It cannot be recovered through the app.'
                        : 'This permanently removes the website login and clears the listed active app data. It cannot be recovered through the app.'}
            </Alert>

            <Box>
                <Typography variant="overline" color="text.secondary">Selected team member</Typography>
                <Typography sx={{ fontWeight: 700 }}>{preview.target?.name || 'Unnamed member'}</Typography>
                <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', overflowWrap: 'anywhere' }}>
                    {preview.target?.email || 'Email unavailable'}
                </Typography>
            </Box>

            {!isAuthOnly && (
                <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.86rem' }}>Active database data</Typography>
                    {categories.length > 0 ? (
                        <List dense disablePadding aria-label="Database deletion categories">
                            {categories.map((category) => (
                                <ListItem key={category.key} disableGutters divider>
                                    <ListItemText
                                        primary={category.label}
                                        secondary={category.policy === 'ANONYMIZE'
                                            ? 'Identifying fields minimized'
                                            : 'Deleted'}
                                    />
                                    <Chip
                                        label={`${formatCount(category.row_count)} rows`}
                                        size="small"
                                        variant="outlined"
                                    />
                                </ListItem>
                            ))}
                        </List>
                    ) : (
                        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mt: 0.5 }}>
                            No active database rows are listed for this target.
                        </Typography>
                    )}
                    <Typography sx={{ fontSize: '0.8rem', mt: 1 }}>
                        Total deleted: <strong>{formatCount(preview.total_delete_rows)}</strong>
                        {' · '}Identifying fields minimized: <strong>{formatCount(preview.total_anonymize_rows)}</strong>
                    </Typography>
                </Box>
            )}

            {warnings.map((warning) => (
                <Alert key={warning} severity="warning">
                    {friendlyCode(warning, WARNING_COPY)}
                </Alert>
            ))}

            {blockers.map((blocker) => (
                <Alert key={blocker} severity="error">
                    <strong>Deletion blocked.</strong> {friendlyCode(blocker, BLOCKER_COPY)}
                </Alert>
            ))}

            {(retained.length > 0 || shared.length > 0) && (
                <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.86rem' }}>What is retained</Typography>
                    <List dense sx={{ listStyleType: 'disc', pl: 2.5, py: 0 }}>
                        {[...retained, ...shared].map((item) => (
                            <ListItem key={item} sx={{ display: 'list-item', py: 0.15, pl: 0 }}>
                                <ListItemText primary={item} primaryTypographyProps={{ fontSize: '0.8rem' }} />
                            </ListItem>
                        ))}
                    </List>
                </Box>
            )}
        </Box>
    );
}

PreviewSummary.propTypes = {
    preview: PropTypes.object.isRequired,
    firebaseWasMissing: PropTypes.bool.isRequired,
};

function ResultSummary({ result }) {
    const database = result?.database || {};
    const deleted = Array.isArray(database.deleted_by_category) ? database.deleted_by_category : [];
    const anonymized = Array.isArray(database.anonymized_by_category) ? database.anonymized_by_category : [];
    const categories = [...deleted, ...anonymized];
    const cleanupPerformed = database.cleanup_performed !== false;

    return (
        <Box sx={{ display: 'grid', gap: 1.25 }}>
            <Alert severity="success">The team member deletion completed.</Alert>
            <Typography sx={{ fontSize: '0.82rem' }}>
                {result?.firebase_was_missing
                    ? 'The website login was already absent. '
                    : 'The website login was removed. '}
                {cleanupPerformed
                    ? 'The approved active database data was cleared.'
                    : 'No database cleanup was performed because no canonical database account was found.'}
            </Typography>
            {categories.length > 0 && (
                <List dense disablePadding aria-label="Completed database categories">
                    {categories.map((category) => (
                        <ListItem key={`${category.key}-${category.label}`} disableGutters divider>
                            <ListItemText primary={category.label} />
                            <Chip label={`${formatCount(category.row_count)} rows`} size="small" variant="outlined" />
                        </ListItem>
                    ))}
                </List>
            )}
            <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                Total deleted: {formatCount(database.total_deleted)} · Identifying fields minimized: {formatCount(database.total_anonymized)}
            </Typography>
        </Box>
    );
}

ResultSummary.propTypes = {
    result: PropTypes.object.isRequired,
};

export default function TeamMemberOffboardingDialog({
    target = null,
    onClose = () => {},
    onCompleted = () => Promise.resolve(),
}) {
    const [phase, setPhase] = useState('idle');
    const [preview, setPreview] = useState(null);
    const [confirmation, setConfirmation] = useState('');
    const [safeError, setSafeError] = useState(null);
    const [result, setResult] = useState(null);
    const [refreshWarning, setRefreshWarning] = useState('');
    const requestGeneration = useRef(0);
    const submittingRef = useRef(false);
    const titleRef = useRef(null);
    const contentRef = useRef(null);

    const loadPreview = useCallback(async () => {
        if (!target?.firebase_uid) return;
        const generation = requestGeneration.current + 1;
        requestGeneration.current = generation;
        setPhase('loading');
        setPreview(null);
        setConfirmation('');
        setSafeError(null);
        setResult(null);
        setRefreshWarning('');
        try {
            const response = await requestTeamMemberDeletionPreview(target.firebase_uid);
            if (requestGeneration.current !== generation) return;
            const nextPreview = validateTeamMemberDeletionPreview(
                response,
                target.firebase_uid,
            );
            setPreview(nextPreview);
            setPhase('ready');
        } catch (error) {
            if (requestGeneration.current !== generation) return;
            if (error instanceof OffboardingContractError) {
                setPreview(null);
                setSafeError({
                    code: error.code,
                    message: error.code === 'PREVIEW_EXPIRED'
                        ? 'The deletion preview expired. Request a fresh preview before continuing.'
                        : 'The server returned an invalid deletion preview. No deletion request was sent.',
                });
                setPhase(error.code === 'PREVIEW_EXPIRED' ? 'refresh-required' : 'preview-error');
                return;
            }
            const parsed = parseOffboardingApiError(error);
            setSafeError(parsed);
            if (parsed.status === 401 || parsed.status === 403) {
                setPhase('authorization-error');
            } else {
                setPhase(FEATURE_DISABLED_CODES.has(parsed.code) ? 'feature-disabled' : 'preview-error');
            }
        }
    }, [target]);

    useEffect(() => {
        if (!target) {
            requestGeneration.current += 1;
            setPhase('idle');
            setPreview(null);
            setConfirmation('');
            setSafeError(null);
            setResult(null);
            submittingRef.current = false;
            return undefined;
        }
        loadPreview();
        return () => {
            requestGeneration.current += 1;
        };
    }, [loadPreview, target]);

    useEffect(() => {
        if (phase !== 'ready' || !preview?.expires_at) return undefined;
        const expiresAt = Date.parse(preview.expires_at);
        if (!Number.isFinite(expiresAt)) return undefined;
        const expire = () => {
            setPreview(null);
            setConfirmation('');
            setSafeError({
                responseReceived: true,
                code: 'PREVIEW_EXPIRED',
                message: 'The deletion preview expired. Request a fresh preview before continuing.',
                requiresFreshPreview: true,
            });
            setPhase('refresh-required');
        };
        const remaining = expiresAt - Date.now();
        if (remaining <= 0) {
            expire();
            return undefined;
        }
        const timeoutId = window.setTimeout(expire, remaining);
        return () => window.clearTimeout(timeoutId);
    }, [phase, preview?.expires_at]);

    useLayoutEffect(() => {
        if (!target || phase === 'idle') return;
        if (contentRef.current) {
            contentRef.current.scrollTop = 0;
        }
        titleRef.current?.focus({ preventScroll: true });
    }, [phase, target]);

    const canExecute = phase === 'ready'
        && preview?.can_execute === true
        && safeList(preview?.blockers).length === 0
        && Boolean(preview?.preview_token)
        && confirmation === preview?.required_confirmation;

    const submitDelete = async (event) => {
        event?.preventDefault?.();
        if (!canExecute || submittingRef.current || !target) return;
        submittingRef.current = true;
        const activePreview = preview;
        const previewToken = activePreview.preview_token;
        setPhase('submitting');
        setSafeError(null);
        try {
            const response = await permanentlyDeleteTeamMember(
                target.firebase_uid,
                previewToken,
                confirmation,
            );
            const validatedResult = validateTeamMemberDeletionResult(response, activePreview);
            setPreview(null);
            setConfirmation('');
            setResult(validatedResult);
            setPhase('success');
            try {
                await onCompleted();
            } catch {
                setRefreshWarning('Deletion completed, but the Team Members list could not be refreshed. Refresh the list manually.');
            }
        } catch (error) {
            if (error instanceof OffboardingContractError) {
                setPreview(null);
                setConfirmation('');
                setSafeError({
                    code: error.code,
                    message: 'The server returned an invalid deletion result.',
                });
                setPhase('indeterminate');
                return;
            }
            const parsed = parseOffboardingApiError(error);
            setPreview(null);
            setConfirmation('');
            setSafeError(parsed);
            if (!parsed.responseReceived) {
                setPhase('indeterminate');
            } else if (parsed.status === 401 || parsed.status === 403) {
                setPhase('authorization-error');
            } else if (parsed.code === FIREBASE_OUTCOME_UNKNOWN_CODE) {
                setPhase('indeterminate');
            } else if (NON_RETRYABLE_CODES.has(parsed.code)) {
                setPhase('reconciliation');
            } else if (parsed.retryable && parsed.requiresFreshPreview) {
                setPhase('retryable');
            } else if (parsed.requiresFreshPreview
                || parsed.code.startsWith('PREVIEW_')
                || FRESH_PREVIEW_CODES.has(parsed.code)) {
                setPhase('refresh-required');
            } else if (FEATURE_DISABLED_CODES.has(parsed.code)) {
                setPhase('feature-disabled');
            } else {
                setPhase('delete-error');
            }
        } finally {
            submittingRef.current = false;
        }
    };

    const closeSafely = (_event, reason) => {
        if (phase === 'submitting') return;
        if (reason === 'backdropClick' && phase === 'indeterminate') return;
        requestGeneration.current += 1;
        onClose();
    };

    const errorMessage = safeError?.message || 'Team-member deletion could not continue safely.';
    const reconciliationLabel = safeError?.reconciliationStatus
        ? safeError.reconciliationStatus.replaceAll('_', ' ')
        : 'receipt ambiguous';

    return (
        <Dialog
            open={Boolean(target)}
            onClose={closeSafely}
            maxWidth="sm"
            fullWidth
            aria-labelledby={TITLE_ID}
            aria-describedby={DESCRIPTION_ID}
            PaperProps={{ sx: { maxHeight: { xs: '94vh', sm: '88vh' } } }}
        >
            <DialogTitle
                id={TITLE_ID}
                ref={titleRef}
                tabIndex={-1}
                autoFocus
                sx={{ fontSize: '1rem', fontWeight: 700 }}
            >
                Permanently delete team member
            </DialogTitle>
            <DialogContent
                ref={contentRef}
                data-testid="team-member-offboarding-content"
                sx={{ display: 'grid', gap: 1.5, pt: 1 }}
            >
                <Typography id={DESCRIPTION_ID} sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
                    Review the server-generated preview and confirm the exact email before deletion.
                </Typography>
                <Divider />

                {phase === 'loading' && (
                    <Box role="status" aria-live="polite" sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 3 }}>
                        <CircularProgress size={22} />
                        <Typography sx={{ fontSize: '0.84rem' }}>Preparing a read-only deletion preview…</Typography>
                    </Box>
                )}

                {phase === 'ready' && preview && (
                    <Box component="form" id="team-member-delete-form" onSubmit={submitDelete} sx={{ display: 'grid', gap: 1.5 }}>
                        <PreviewSummary
                            preview={preview}
                            firebaseWasMissing={Boolean(target?.firebase_missing)}
                        />
                        <TextField
                            label="Type the team member email to confirm"
                            value={confirmation}
                            onChange={(event) => setConfirmation(event.target.value)}
                            helperText={`Enter exactly: ${preview.required_confirmation}`}
                            autoComplete="off"
                            inputProps={{ spellCheck: false }}
                            fullWidth
                            size="small"
                        />
                    </Box>
                )}

                {phase === 'preview-error' && (
                    <Alert severity="error" role="alert">{errorMessage}</Alert>
                )}

                {phase === 'refresh-required' && (
                    <Alert severity="warning" role="alert">
                        {errorMessage}
                    </Alert>
                )}

                {phase === 'retryable' && (
                    <Alert severity="warning" role="alert">
                        {errorMessage}{' '}
                        {safeError?.firebaseAbsent
                            ? 'Authentication was already absent before this attempt.'
                            : 'Authentication was not changed.'}
                    </Alert>
                )}

                {phase === 'reconciliation' && (
                    <Alert severity="error" role="alert">
                        The database outcome is not safe to retry. Manual reconciliation is required and no automatic
                        retry will be attempted. Reconciliation status: <strong>{reconciliationLabel}</strong>.
                    </Alert>
                )}

                {phase === 'indeterminate' && (
                    <Alert severity="error" role="alert">
                        {safeError?.code === FIREBASE_OUTCOME_UNKNOWN_CODE
                            ? 'Application data was deleted, but authentication deletion could not be confirmed. Do not submit this deletion again. An administrator must reconcile the Firebase identity before taking another action.'
                            : safeError?.code === 'INVALID_DELETE_RESPONSE'
                            ? 'The final server response could not be verified. '
                            : 'No final server response was received. '}
                        {safeError?.code !== FIREBASE_OUTCOME_UNKNOWN_CODE
                            && 'The operation may still have completed or may still be running. Do not submit it again. An administrator must manually reconcile the member and database state before taking another action.'}
                    </Alert>
                )}

                {phase === 'feature-disabled' && (
                    <Alert severity="info" role="status">
                        Team-member permanent deletion is currently disabled by the server. No deletion request will be retried.
                    </Alert>
                )}

                {phase === 'authorization-error' && (
                    <Alert severity="error" role="alert">
                        You are not authorized to preview or permanently delete this team member. The server did not
                        authorize the operation, and no automatic retry will be attempted.
                    </Alert>
                )}

                {phase === 'delete-error' && (
                    <Alert severity="error" role="alert">
                        {errorMessage} No automatic retry will be attempted.
                    </Alert>
                )}

                {phase === 'submitting' && (
                    <Box role="status" aria-live="polite" sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 3 }}>
                        <CircularProgress size={22} />
                        <Typography sx={{ fontSize: '0.84rem' }}>
                            Permanently deleting user and clearing app data…
                        </Typography>
                    </Box>
                )}

                {phase === 'success' && result && <ResultSummary result={result} />}
                {refreshWarning && <Alert severity="warning">{refreshWarning}</Alert>}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 0.75 }}>
                {phase === 'ready' && (
                    <>
                        <Button onClick={closeSafely}>Cancel</Button>
                        <Button
                            type="submit"
                            form="team-member-delete-form"
                            variant="contained"
                            color="error"
                            disabled={!canExecute}
                        >
                            Delete permanently
                        </Button>
                    </>
                )}
                {phase === 'preview-error' && (
                    <>
                        <Button onClick={closeSafely}>Cancel</Button>
                        <Button onClick={loadPreview} variant="outlined">Retry preview</Button>
                    </>
                )}
                {(phase === 'refresh-required' || phase === 'retryable') && (
                    <>
                        <Button onClick={closeSafely}>Cancel</Button>
                        <Button onClick={loadPreview} variant="outlined">Request fresh preview</Button>
                    </>
                )}
                {phase === 'submitting' && <Button disabled>Deletion in progress</Button>}
                {['reconciliation', 'indeterminate', 'feature-disabled', 'authorization-error', 'delete-error'].includes(phase) && (
                    <Button onClick={closeSafely}>Close</Button>
                )}
                {phase === 'success' && <Button onClick={closeSafely} autoFocus>Done</Button>}
            </DialogActions>
        </Dialog>
    );
}

TeamMemberOffboardingDialog.propTypes = {
    target: PropTypes.object,
    onClose: PropTypes.func,
    onCompleted: PropTypes.func,
};
